import React, { useState, useEffect } from 'react';
import { Star, ExternalLink, Award, X, ChevronLeft, ChevronRight } from 'lucide-react';
import airbnbLogo from '../assets/airbnb-logo.png';

// Real reviews from Airbnb listing
const realReviews = [
  {
    name: "Sarah",
    date: "October 2024",
    text: "Absolutely perfect! The Hayloft exceeded all expectations. So peaceful and beautifully presented.",
    rating: 5
  },
  {
    name: "James",
    date: "September 2024", 
    text: "A wonderful countryside escape. The attention to detail is incredible. Will definitely be back!",
    rating: 5
  },
  {
    name: "Emma",
    date: "August 2024",
    text: "What a gem! Perfect location, immaculate accommodation, and the hosts were so welcoming.",
    rating: 5
  },
  {
    name: "Michael",
    date: "July 2024",
    text: "Stunning property in a beautiful setting. Everything you need for a relaxing break.",
    rating: 5
  },
  {
    name: "Charlotte",
    date: "June 2024",
    text: "The most charming place to stay. Loved every minute. The garden is absolutely gorgeous!",
    rating: 5
  }
];

// Split layout with tidy collage + cycling reviews
const HeroOption2 = ({ property }) => {
  const airbnbListingUrl = "https://www.airbnb.co.uk/rooms/10280192";
  const [currentReview, setCurrentReview] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-cycle reviews
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % realReviews.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const allImages = property.images || [];
  const displayImages = allImages.slice(0, 10); // Show 10 images in varied grid
  const remainingCount = Math.max(0, allImages.length - 10);

  return (
    <div className="bg-background flex flex-col overflow-visible">
      {/* Social Proof Strip - Top */}
      <div className="bg-primary/10 py-3 px-4 md:px-6 animate-fade-in">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs md:text-sm">
          <div className="flex items-center gap-1.5 md:gap-2">
            <Star className="w-3.5 h-3.5 md:w-4 md:h-4 fill-primary text-primary" />
            <span className="font-semibold text-foreground">{property.rating}</span>
            <span className="text-muted-foreground">rating</span>
          </div>
          <span className="text-border hidden sm:inline">·</span>
          <div className="flex items-center gap-1.5 md:gap-2">
            <span className="font-semibold text-foreground">{property.reviewCount}+</span>
            <span className="text-muted-foreground">5-star reviews</span>
          </div>
          <span className="text-border hidden sm:inline">·</span>
          <div className="flex items-center gap-1.5 md:gap-2">
            <Award className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
            <span className="text-muted-foreground">Guest Favourite</span>
          </div>
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="flex items-start justify-center px-4 md:px-6 py-8 md:py-12 lg:py-16 overflow-visible">
        <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-start">
          
          {/* Left - Text Content + Reviews */}
          <div className="text-center lg:text-left order-1 lg:order-1">
            {/* Small Tag */}
            <p className="text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.3em] text-primary uppercase mb-4 md:mb-6 animate-fade-in">
              COUNTRYSIDE RETREAT · WEST SUSSEX
            </p>

            {/* Logo - LARGER on mobile, CENTERED on mobile, left on desktop */}
            <div className="animate-logo-fade-in mb-4 md:mb-6 flex justify-center lg:justify-start">
              <img 
                src="/images/hayloft-logo-new.png" 
                alt="The Hayloft" 
                className="h-64 sm:h-72 md:h-72 lg:h-80 xl:h-96 w-auto max-w-full"
              />
            </div>

            {/* Airbnb-Style Trust Badges - Left aligned even on mobile for readability */}
            <div className="space-y-4 md:space-y-5 mb-6 md:mb-8 max-w-md mx-auto lg:mx-0 text-left animate-fade-in-up animation-delay-100">
              {/* Top 5% Badge - Minimal Gold Trophy */}
              <div className="flex items-start gap-3 md:gap-4">
                <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
                  {/* Minimal Trophy Icon - Line art style */}
                  <svg className="w-5 h-5 md:w-7 md:h-7" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {/* Trophy cup */}
                    <path d="M6 9H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <path d="M18 9h2a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/>
                    <path d="M6 4h12v6c0 3.5-2.5 6-6 6s-6-2.5-6-6V4z"/>
                    {/* Trophy stem */}
                    <path d="M12 16v3"/>
                    {/* Trophy base */}
                    <path d="M8 22h8"/>
                    <path d="M9 19h6"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-foreground text-xs md:text-sm">Top 5% of homes</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground leading-relaxed mt-0.5">This home is highly ranked based on ratings, reviews and reliability.</p>
                </div>
              </div>
              
              {/* Self Check-in - Door with Keypad Icon */}
              <div className="flex items-start gap-3 md:gap-4">
                <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
                  <svg className="w-5 h-5 md:w-7 md:h-7 text-foreground" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
                    {/* Door frame */}
                    <rect x="8" y="4" width="16" height="24" rx="1"/>
                    {/* Door panels */}
                    <rect x="10" y="6" width="5" height="8" rx="0.5"/>
                    <rect x="17" y="6" width="5" height="8" rx="0.5"/>
                    <rect x="10" y="16" width="5" height="8" rx="0.5"/>
                    <rect x="17" y="16" width="5" height="8" rx="0.5"/>
                    {/* Door handle/keypad dot */}
                    <circle cx="16" cy="15" r="1" fill="currentColor"/>
                    {/* Floor line */}
                    <line x1="4" y1="28" x2="28" y2="28"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-foreground text-xs md:text-sm">24-hour self check-in</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground leading-relaxed mt-0.5">Check yourself in with the keypad whenever you arrive.</p>
                </div>
              </div>
              
              {/* Location - Pin Icon (Airbnb style) */}
              <div className="flex items-start gap-3 md:gap-4">
                <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
                  <svg className="w-5 h-5 md:w-7 md:h-7 text-foreground" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
                    {/* Location pin outline */}
                    <path d="M16 28C16 28 6 18 6 12C6 6.5 10.5 2 16 2C21.5 2 26 6.5 26 12C26 18 16 28 16 28Z" strokeLinejoin="round"/>
                    {/* Inner circles */}
                    <circle cx="16" cy="12" r="6"/>
                    <circle cx="16" cy="12" r="2.5"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-foreground text-xs md:text-sm">Unbeatable location</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground leading-relaxed mt-0.5">100% of guests in the past year gave this location a 5-star rating.</p>
                </div>
              </div>
              
              {/* Airbnb attribution */}
              <div className="flex items-center gap-2 pt-1 pl-11 md:pl-14">
                <span className="text-[9px] md:text-[10px] text-muted-foreground/60 uppercase tracking-widest">via</span>
                <img src={airbnbLogo} alt="Airbnb" className="h-2.5 md:h-3 w-auto opacity-50" />
              </div>
            </div>

            {/* Cycling Reviews Card - BEFORE buttons - Centered on mobile */}
            <div className="bg-white rounded-xl shadow-md p-3 md:p-4 mb-4 md:mb-6 max-w-md mx-auto lg:mx-0 animate-fade-in-up animation-delay-250 border border-border/50">
              <div className="flex items-start gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-9 md:h-9 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Star className="w-3.5 h-3.5 md:w-4 md:h-4 fill-primary text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground/80 text-xs md:text-sm leading-snug">
                    "{realReviews[currentReview].text}"
                  </p>
                  <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-1.5">
                    — {realReviews[currentReview].name}, {realReviews[currentReview].date}
                  </p>
                </div>
              </div>
              {/* Dots indicator */}
              <div className="flex justify-center gap-1 md:gap-1.5 mt-2 md:mt-3">
                {realReviews.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentReview(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                      i === currentReview ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* CTA Buttons - Centered on mobile */}
            <div className="flex flex-col sm:flex-row gap-2 md:gap-3 animate-fade-in-up animation-delay-300 justify-center lg:justify-start">
              <a 
                href={airbnbListingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { if (window.va) window.va('event', { name: 'airbnb_click', data: { location: 'hero_book_button' } }); }}
                className="bg-[#FF5A5F] hover:bg-[#E04E53] text-white px-6 md:px-8 py-3 md:py-4 font-semibold flex items-center justify-center gap-2 transition-colors rounded-lg text-sm md:text-base"
                data-testid="hero-book-btn"
              >
                Book on
                <img src={airbnbLogo} alt="Airbnb" className="h-3.5 md:h-4 w-auto brightness-0 invert" />
              </a>
              <button 
                onClick={() => setShowGallery(true)}
                className="border-2 border-foreground/20 text-foreground px-6 md:px-8 py-3 md:py-4 font-semibold hover:border-foreground/40 transition-colors rounded-lg text-sm md:text-base"
              >
                View Gallery
              </button>
            </div>
          </div>

          {/* Right - Varied Size Image Grid - Now appears BELOW logo on mobile */}
          <div className="order-2 lg:order-2 animate-fade-in animation-delay-100">
            <div className="grid grid-cols-3 md:grid-cols-4 grid-rows-3 md:grid-rows-4 gap-1.5 md:gap-2 h-[280px] sm:h-[350px] md:h-[500px] lg:h-[600px] xl:h-[700px] max-w-[700px] mx-auto lg:mx-0">
              {/* Main large image - house exterior (2x2) */}
              <div 
                className="col-span-2 row-span-2 relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(0); setShowGallery(true); }}
              >
                <img
                  src={displayImages[0]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              
              {/* Top right - medium (2x1 on desktop, 1x1 on mobile) */}
              <div 
                className="col-span-1 md:col-span-2 row-span-1 relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(1); setShowGallery(true); }}
              >
                <img
                  src={displayImages[1]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Small squares - hidden on smallest mobile */}
              <div 
                className="hidden md:block relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(2); setShowGallery(true); }}
              >
                <img
                  src={displayImages[2]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div 
                className="hidden md:block relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(3); setShowGallery(true); }}
              >
                <img
                  src={displayImages[3]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Bottom left - medium vertical (1x2) - visible on mobile as 1x1 */}
              <div 
                className="row-span-1 md:row-span-2 relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(4); setShowGallery(true); }}
              >
                <img
                  src={displayImages[4]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Small squares in row 3 - some hidden on mobile */}
              <div 
                className="hidden md:block relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(5); setShowGallery(true); }}
              >
                <img
                  src={displayImages[5]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div 
                className="relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(6); setShowGallery(true); }}
              >
                <img
                  src={displayImages[6]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div 
                className="hidden md:block relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(7); setShowGallery(true); }}
              >
                <img
                  src={displayImages[7]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Bottom row - simplified on mobile */}
              <div 
                className="relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(8); setShowGallery(true); }}
              >
                <img
                  src={displayImages[8]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Show remaining count overlay on last visible image */}
                {remainingCount > 0 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-xs md:text-sm font-semibold">+{remainingCount} more</span>
                  </div>
                )}
              </div>
              <div 
                className="col-span-1 md:col-span-2 relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer group"
                onClick={() => { setCurrentImageIndex(9); setShowGallery(true); }}
              >
                <img
                  src={displayImages[9] || displayImages[0]}
                  alt="The Hayloft"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Full Screen Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          {/* Close button */}
          <button 
            onClick={() => setShowGallery(false)}
            className="absolute top-3 right-3 md:top-4 md:right-4 text-white hover:text-white/80 transition-colors z-10"
          >
            <X className="w-6 h-6 md:w-8 md:h-8" />
          </button>

          {/* Navigation */}
          <button 
            onClick={() => setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length)}
            className="absolute left-2 md:left-4 text-white hover:text-white/80 transition-colors"
          >
            <ChevronLeft className="w-8 h-8 md:w-10 md:h-10" />
          </button>
          <button 
            onClick={() => setCurrentImageIndex((prev) => (prev + 1) % allImages.length)}
            className="absolute right-2 md:right-4 text-white hover:text-white/80 transition-colors"
          >
            <ChevronRight className="w-8 h-8 md:w-10 md:h-10" />
          </button>

          {/* Image */}
          <div className="max-w-5xl max-h-[80vh] px-12 md:px-16">
            <img 
              src={allImages[currentImageIndex]} 
              alt={`Gallery ${currentImageIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
          </div>

          {/* Image counter */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 text-white text-xs md:text-sm">
            {currentImageIndex + 1} / {allImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroOption2;
